import { format } from 'date-fns'
import { AlertTriangle, CalendarClock, Power, PowerOff } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Badge } from '@/components/admin/shared/badge'
import { Modal } from '@/components/admin/shared/modal'
import { useRecommendationEngineMock } from './recommendation-engine-context'

function isoDay(date: Date | string): string {
  const normalized = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(normalized.getTime())) return ''
  return normalized.toISOString().slice(0, 10)
}

export function BaselineEpochPanel() {
  const {
    baselineEpochAt,
    baselineEpochBusy,
    baselineEpochTimeZone,
    engineEnabled,
    setBaselineEpoch,
    updateBaselineEpoch,
    clearBaselineEpoch,
  } = useRecommendationEngineMock()

  const [dateValue, setDateValue] = useState(
    baselineEpochAt ? isoDay(baselineEpochAt) : '',
  )
  const [confirming, setConfirming] = useState<
    null | 'set' | 'update' | 'clear'
  >(null)

  const mode = baselineEpochAt ? 'update' : 'set'
  const canSetOrUpdate = dateValue.trim().length > 0 && !baselineEpochBusy
  const canClear = baselineEpochAt !== null && !baselineEpochBusy

  const subtitle = useMemo(() => {
    if (!baselineEpochAt) {
      return 'Engine is gated off until a starting date is selected.'
    }
    return `Current start date: ${format(baselineEpochAt, 'MMM d, yyyy')}`
  }, [baselineEpochAt])

  async function handleProceed() {
    const action = confirming
    if (!action) return
    setConfirming(null)
    if (action === 'set') {
      await setBaselineEpoch(dateValue)
      return
    }
    if (action === 'update') {
      await updateBaselineEpoch(dateValue)
      return
    }
    await clearBaselineEpoch()
    setDateValue('')
  }

  return (
    <div className="border-border rounded-lg border p-4">
      <div className="mb-2 flex items-center gap-2">
        <div className="bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 rounded-md p-1.5">
          <CalendarClock size={16} />
        </div>
        <h3 className="text-foreground text-sm font-semibold">
          Baseline epoch
        </h3>
        {engineEnabled ? (
          <Badge variant="active">
            <span className="inline-flex items-center gap-1">
              <Power size={12} />
              Engine on
            </span>
          </Badge>
        ) : (
          <Badge variant="paused">
            <span className="inline-flex items-center gap-1">
              <PowerOff size={12} />
              Engine off
            </span>
          </Badge>
        )}
      </div>
      <p className="text-muted-foreground text-xs">{subtitle}</p>
      <p className="text-muted-foreground mt-1 text-xs">
        Baseline epoch is the contractual t=0 boundary. Work before this date is
        ignored by the recommendation engine. Setting, updating, or clearing
        this date does not run processing jobs automatically.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
        <input
          type="date"
          value={dateValue}
          onChange={(e) => setDateValue(e.target.value)}
          aria-label="Baseline epoch date"
          className="bg-muted border-border text-foreground w-full rounded-lg border px-3 py-2 text-sm"
          disabled={baselineEpochBusy}
        />
        <button
          type="button"
          disabled={!canSetOrUpdate}
          onClick={() => setConfirming(mode)}
          aria-label={
            mode === 'set' ? 'Set baseline epoch' : 'Update baseline epoch'
          }
          className="bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {mode === 'set' ? 'Set' : 'Update'}
        </button>
        <button
          type="button"
          disabled={!canClear}
          onClick={() => setConfirming('clear')}
          aria-label="Clear baseline epoch"
          className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg border border-transparent px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        >
          Clear
        </button>
      </div>

      <p className="text-muted-foreground/80 mt-2 text-[11px]">
        Time zone: {baselineEpochTimeZone ?? 'UTC fallback'}
      </p>

      <Modal
        isOpen={confirming !== null}
        onClose={() => setConfirming(null)}
        title={
          confirming === 'clear'
            ? 'Clear baseline epoch?'
            : confirming === 'set'
              ? 'Set baseline epoch?'
              : 'Update baseline epoch?'
        }
      >
        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
            <AlertTriangle
              size={16}
              className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"
            />
            <p className="text-sm whitespace-pre-line">
              {
                'This epoch date directly impacts the FSRS and Recommendation Engine:\n1. Setting baseline epoch changes the processing boundary for what counts as client source material.\n2. Setting the date value does not automatically trigger a reprocessing.\n3. When baseline epoch is cleared, the recommendation engine is turned off and no data that was previously processed will be deleted or modified.'
              }
            </p>
          </div>
          <div className="border-border flex justify-end gap-2 border-t pt-3">
            <button
              type="button"
              onClick={() => setConfirming(null)}
              className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-md px-3 py-1.5 text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleProceed()}
              className="bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-90"
            >
              Proceed
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
